export type ContactLeadSource = "landing" | "contact-page";

export interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone?: string;
  message?: string;
  leadSource: ContactLeadSource;
  language: string;
  privacyConsentText: string;
  oneToOneConsentText: string;
  marketingConsent: boolean;
  marketingConsentText: string;
}

interface HubSpotField {
  objectTypeId: string;
  name: string;
  value: string;
}

interface HubSpotCommunicationConsent {
  value: boolean;
  subscriptionTypeId: number;
  text: string;
}

interface HubSpotLegalConsent {
  consentToProcess: boolean;
  text: string;
  communications?: HubSpotCommunicationConsent[];
}

interface HubSpotSubmission {
  fields: HubSpotField[];
  context: {
    pageUri: string;
    pageName: string;
  };
  legalConsentOptions: {
    consent: HubSpotLegalConsent;
  };
}

const HUBSPOT_PORTAL_ID = import.meta.env.VITE_HUBSPOT_PORTAL_ID;
const HUBSPOT_FORM_ID = import.meta.env.VITE_HUBSPOT_FORM_ID;
const HUBSPOT_MARKETING_SUBSCRIPTION_TYPE_ID = import.meta.env
  .VITE_HUBSPOT_MARKETING_SUBSCRIPTION_TYPE_ID;
const HUBSPOT_ONE_TO_ONE_SUBSCRIPTION_TYPE_ID = import.meta.env
  .VITE_HUBSPOT_ONE_TO_ONE_SUBSCRIPTION_TYPE_ID;

const CONTACT = "0-1";
const COMPANY = "0-2";

export function isHubSpotConfigured(): boolean {
  return Boolean(HUBSPOT_PORTAL_ID && HUBSPOT_FORM_ID);
}

function contactField(name: string, value: string): HubSpotField {
  return { objectTypeId: CONTACT, name, value };
}

function companyField(name: string, value: string): HubSpotField {
  return { objectTypeId: COMPANY, name, value };
}

function buildLegalConsent(data: ContactFormData): HubSpotLegalConsent {
  const communications: HubSpotCommunicationConsent[] = [];

  if (HUBSPOT_ONE_TO_ONE_SUBSCRIPTION_TYPE_ID) {
    communications.push({
      value: true,
      subscriptionTypeId: Number(HUBSPOT_ONE_TO_ONE_SUBSCRIPTION_TYPE_ID),
      text: data.oneToOneConsentText,
    });
  }

  if (data.marketingConsent && HUBSPOT_MARKETING_SUBSCRIPTION_TYPE_ID) {
    communications.push({
      value: true,
      subscriptionTypeId: Number(HUBSPOT_MARKETING_SUBSCRIPTION_TYPE_ID),
      text: data.marketingConsentText,
    });
  }

  const consent: HubSpotLegalConsent = {
    consentToProcess: true,
    text: data.privacyConsentText,
  };

  if (communications.length > 0) {
    consent.communications = communications;
  }

  return consent;
}

function buildSubmission(data: ContactFormData): HubSpotSubmission {
  const fields: HubSpotField[] = [
    contactField("firstname", data.firstName),
    contactField("lastname", data.lastName),
    contactField("email", data.email),
    companyField("name", data.company),
    contactField("lead_source", data.leadSource),
    contactField("hs_language", data.language),
  ];

  if (data.phone?.trim()) {
    fields.push(contactField("phone", data.phone.trim()));
  }
  if (data.message?.trim()) {
    fields.push(contactField("message", data.message.trim()));
  }

  return {
    fields,
    context: {
      pageUri: window.location.href,
      pageName: data.leadSource === "contact-page" ? "Contact" : "Landing",
    },
    legalConsentOptions: {
      consent: buildLegalConsent(data),
    },
  };
}

export async function submitContactForm(data: ContactFormData): Promise<void> {
  if (!isHubSpotConfigured()) {
    throw new Error("hubspot_not_configured");
  }

  const response = await fetch(
    `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildSubmission(data)),
    },
  );

  if (!response.ok) {
    const body = await response.text();
    console.error("HubSpot form submission failed:", body);
    throw new Error("hubspot_submit_failed");
  }
}
